import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Button, Container, Grid, Paper, Stack, IconButton, CircularProgress } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import StorageIcon from "@mui/icons-material/Storage";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import CloudDoneIcon from "@mui/icons-material/CloudDone";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { supabase } from "../../lib/supabase";
import EditableImage from "../../components/ui/EditableImage";
import SEO from "../../components/seo/SEO";
import profile from "../../assets/profile11.jpg"; 

const HomePage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('activities')
          .select(`
            *,
            translations:activities_translations(*)
          `)
          .eq('is_published', true)
          .order('display_order', { ascending: true })
          .order('date', { ascending: false });

        if (error) throw error;

        const currentLang = i18n.language || 'en';
        const localized = data.map(act => {
          const trans = act.translations?.find(t => t.language_code === currentLang) || 
                        act.translations?.find(t => t.language_code === 'en') || 
                        act.translations?.[0] || {};
          return {
            ...act,
            title: trans.title || '',
            description: trans.description || '',
            tagline: trans.tagline || ''
          };
        });
        setActivities(localized);
      } catch (err) {
        console.error("Error fetching activities for home:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchActivities();
  }, [i18n.language]);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % activities.length);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + activities.length) % activities.length);
  };

  const getVisibleActivities = () => {
    if (activities.length === 0) return [];
    const items = [];
    for (let i = 0; i < 3; i++) {
      items.push(activities[(currentIndex + i) % activities.length]);
    }
    return items;
  };

  const expertisePillars = [
    {
      icon: <AnalyticsIcon sx={{ fontSize: 40, color: "#D4AF37" }} />,
      title: t("home.expertise.pillars.dataScience.title"),
      description: t("home.expertise.pillars.dataScience.description")
    },
    {
      icon: <SettingsSuggestIcon sx={{ fontSize: 40, color: "#D4AF37" }} />,
      title: t("home.expertise.pillars.smartApis.title"),
      description: t("home.expertise.pillars.smartApis.description")
    },
    {
      icon: <StorageIcon sx={{ fontSize: 40, color: "#D4AF37" }} />,
      title: t("home.expertise.pillars.robustArchitectures.title"),
      description: t("home.expertise.pillars.robustArchitectures.description")
    },
    {
      icon: <CloudDoneIcon sx={{ fontSize: 40, color: "#D4AF37" }} />,
      title: t("home.expertise.pillars.cloudDeployment.title"),
      description: t("home.expertise.pillars.cloudDeployment.description")
    }
  ];

  const dateLocale = i18n.language?.startsWith("fr") ? "fr-FR" : "en-US";

  return (
    <Box component="main" id="main-content">
      <SEO 
        title={t('home.seo.title', { defaultValue: 'Accueil' })}
        description={t('home.seo.description', { defaultValue: 'Alexandre SANOU, Full Stack Data Scientist spécialisé en IA et Machine Learning. Consultant freelance à Paris.' })}
      />
      {/* Hero Section */}
      <Box className="relative min-h-[90vh] flex items-center overflow-hidden bg-[#111827]">
        <Container maxWidth="lg" className="relative z-10 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <Typography
              variant="overline"
              sx={{ 
                color: "#D4AF37", 
                fontWeight: 800, 
                letterSpacing: 3, 
                mb: 1, 
                display: 'block',
                fontSize: '0.9rem',
                textTransform: 'uppercase',
                background: 'linear-gradient(90deg, #D4AF37 0%, #B8962E 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                borderLeft: '4px solid #D4AF37',
                pl: 2
              }}
            >
              {t("home.hero.overlinePrefix")}{" "}
              <span style={{ color: '#0FA3A3', WebkitTextFillColor: '#0FA3A3' }}>
                {t("home.hero.overlineHighlight")}
              </span>
            </Typography>
            
            <Typography 
              variant="h1" 
              sx={{ 
                color: 'white',
                mb: 3,
                fontFamily: 'Philosopher',
                lineHeight: 1.1,
                fontSize: { xs: '2.5rem', md: '4rem' }
              }}
            >
              Alexandre SANOU <br/>
              <span style={{ fontSize: '0.65em' }} className="text-blue-400">
                {t("home.hero.tagline")}
              </span>
            </Typography>
            
            <Typography 
              variant="h5" 
              sx={{ 
                color: '#9CA3AF',
                mb: 4,
                fontWeight: 300,
                lineHeight: 1.6,
                maxWidth: '800px'
              }}
            >
              {t("home.hero.description")}
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mb: 8 }}>
              <Button 
                variant="contained" 
                size="large"
                component="a"
                href="/contact"
                sx={{
                  bgcolor: '#D4AF37',
                  color: '#111827',
                  px: 5,
                  py: 2,
                  borderRadius: '12px',
                  fontSize: '1.125rem',
                  fontWeight: 700,
                  boxShadow: '0 10px 15px -3px rgba(212, 175, 55, 0.3)',
                  '&:hover': {
                    bgcolor: '#B8962E',
                    transform: 'translateY(-4px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                {t("home.hero.primaryCta")}
              </Button>
              <Button 
                variant="outlined" 
                size="large"
                component="a"
                href="/projects"
                sx={{
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  px: 5,
                  py: 2,
                  borderRadius: '12px',
                  fontSize: '1.125rem',
                  fontWeight: 700,
                  backdropFilter: 'blur(4px)',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                    transform: 'translateY(-4px)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                {t("home.hero.secondaryCta")}
              </Button>
            </Stack>

            <Grid container spacing={4} sx={{ borderTop: '1px solid rgba(255,255,255,0.1)', pt: 6 }}>
              <Grid item xs={6} md={3}>
                <Typography variant="h4" sx={{ color: '#0FA3A3', fontWeight: 700, mb: 1 }}>5+</Typography>
                <Typography variant="body2" sx={{ color: '#9CA3AF' }}>{t("home.stats.years.label")}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="h4" sx={{ color: '#0FA3A3', fontWeight: 700, mb: 1 }}>{t("home.stats.endToEnd.value")}</Typography>
                <Typography variant="body2" sx={{ color: '#9CA3AF' }}>{t("home.stats.endToEnd.label")}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="h4" sx={{ color: '#0FA3A3', fontWeight: 700, mb: 1 }}>{t("home.stats.product.value")}</Typography>
                <Typography variant="body2" sx={{ color: '#9CA3AF' }}>{t("home.stats.product.label")}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="h4" sx={{ color: '#0FA3A3', fontWeight: 700, mb: 1 }}>{t("home.stats.saas.value")}</Typography>
                <Typography variant="body2" sx={{ color: '#9CA3AF' }}>{t("home.stats.saas.label")}</Typography>
              </Grid>
            </Grid>
          </motion.div>
        </Container>

        {/* Profile Image - Absolute Positioned for B2B Premium Look */}
        <Box 
          sx={{ 
            position: 'absolute', 
            top: '50%', 
            right: { xs: '-10%', md: '5%' }, 
            transform: 'translateY(-50%)',
            width: { xs: '300px', md: '450px' },
            height: { xs: '300px', md: '450px' },
            display: { xs: 'none', lg: 'block' },
            zIndex: 5
          }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8, rotate: 5 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1.2, delay: 0.3 }}
            style={{ width: '100%', height: '100%', position: 'relative' }}
          >
            {/* Decorative Rings */}
            <Box sx={{ 
              position: 'absolute', 
              inset: -20, 
              border: '1px solid rgba(212, 175, 55, 0.2)', 
              borderRadius: '24px',
              transform: 'rotate(3deg)'
            }} />
            <Box sx={{ 
              position: 'absolute', 
              inset: -10, 
              border: '2px solid rgba(15, 163, 163, 0.3)', 
              borderRadius: '24px',
              transform: 'rotate(-2deg)'
            }} />
            
            <Box 
              component="img"
              src={profile}
              alt="Alexandre SANOU"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '24px',
                boxShadow: '0 30px 60px rgba(0,0,0,0.5)',
                filter: 'grayscale(20%)',
                '&:hover': {
                  filter: 'grayscale(0%)',
                  transform: 'scale(1.02)',
                  transition: 'all 0.5s ease'
                }
              }}
            />
          </motion.div>
        </Box>
        
        {/* Background Decor */}
        <Box className="absolute top-0 right-0 w-1/2 h-full opacity-10 pointer-events-none">
          <div className="absolute top-1/4 right-0 w-96 h-96 bg-blue-500 rounded-full blur-[120px]" />
          <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-yellow-500 rounded-full blur-[100px] opacity-50" />
        </Box>
      </Box>

      {/* Expertise Pillars Section */}
      <Box sx={{ py: 15, bgcolor: '#0B0F14' }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h2" 
            align="center" 
            sx={{ color: 'white', mb: 2, fontFamily: 'Philosopher' }}
          >
            {t("home.expertise.titlePrefix")} <span style={{ color: '#D4AF37' }}>{t("home.expertise.titleHighlight")}</span>
          </Typography>
          <Typography 
            variant="h6" 
            align="center" 
            sx={{ color: '#9CA3AF', mb: 8, maxWidth: '700px', mx: 'auto', fontWeight: 300 }}
          >
            {t("home.expertise.subtitle")}
          </Typography>

          <Grid container spacing={4}>
            {expertisePillars.map((pillar, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 4, 
                    height: '100%', 
                    bgcolor: '#1F2937', 
                    borderRadius: '16px',
                    border: '1px solid rgba(255,255,255,0.05)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-10px)',
                      borderColor: '#D4AF37',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
                    }
                  }}
                >
                  <Box sx={{ mb: 3 }}>{pillar.icon}</Box>
                  <Typography variant="h5" sx={{ color: 'white', mb: 2, fontWeight: 700 }}>
                    {pillar.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#9CA3AF', lineHeight: 1.6 }}>
                    {pillar.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mt: 10, textAlign: 'center' }}>
            <Button 
              variant="text" 
              size="large"
              component="a"
              href="/services"
              sx={{ 
                color: '#D4AF37', 
                fontSize: '1.25rem', 
                fontWeight: 600,
                '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }
              }}
              >
              {t("home.expertise.cta")}
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Activities Carousel Section */}
      <Box sx={{ py: 15, bgcolor: '#111827', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        <Container maxWidth="lg">
          <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mb: 8 }}>
            <Box>
              <Typography 
                variant="overline" 
                sx={{ color: '#0FA3A3', fontWeight: 700, letterSpacing: 2 }}
              >
                {t("home.activities.overline")}
              </Typography>
              <Typography 
                variant="h2" 
                sx={{ color: 'white', mt: 1, fontFamily: 'Philosopher' }}
              >
                {t("home.activities.titlePrefix")} <span style={{ color: '#D4AF37' }}>{t("home.activities.titleHighlight")}</span>
              </Typography>
            </Box>
            <Stack direction="row" spacing={2} alignItems="center">
              <IconButton 
                onClick={handlePrev} 
                sx={{ color: 'white', border: '1px solid rgba(255,255,255,0.1)', '&:hover': { borderColor: '#D4AF37' } }}
              >
                <ArrowBackIosNewIcon />
              </IconButton>
              <IconButton 
                onClick={handleNext} 
                sx={{ color: 'white', border: '1px solid rgba(255,255,255,0.1)', '&:hover': { borderColor: '#D4AF37' } }}
              >
                <ArrowForwardIosIcon />
              </IconButton>
              <Button 
                variant="outlined"
                onClick={() => navigate('/activities')}
                sx={{ 
                  color: 'white', 
                  borderColor: 'rgba(255,255,255,0.2)',
                  borderRadius: '10px',
                  px: 3,
                  display: { xs: 'none', sm: 'inline-flex' },
                  '&:hover': { borderColor: '#D4AF37', color: '#D4AF37' }
                }}
              >
                {t("home.activities.viewAll")}
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ position: 'relative' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                <CircularProgress sx={{ color: '#0FA3A3' }} />
              </Box>
            ) : (
              <Grid container spacing={4}>
                <AnimatePresence mode="popLayout">
                  {getVisibleActivities().map((activity, idx) => (
                    <Grid item xs={12} sm={6} md={4} key={`${activity.id}-${currentIndex}-${idx}`}>
                      <Box 
                        component={motion.div}
                        layout
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -50 }}
                        transition={{ duration: 0.5 }}
                        whileHover={{ y: -10 }}
                        sx={{ 
                          bgcolor: '#1F2937',
                          borderRadius: '20px',
                          overflow: 'hidden',
                          border: '1px solid rgba(255,255,255,0.05)',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            borderColor: 'rgba(212, 175, 55, 0.5)',
                            boxShadow: '0 20px 40px rgba(0,0,0,0.4)'
                          }
                        }}
                      >
                        <Box sx={{ height: '220px', width: '100%', position: 'relative', overflow: 'hidden' }}>
                          <EditableImage 
                            src={activity.cover_image || activity.image_url?.[0] || 'https://via.placeholder.com/400x220?text=Activity'}
                            alt={activity.title}
                            tableName="activities"
                            recordId={activity.id}
                            sx={{ width: '100%', height: '100%' }}
                            imgSx={{ filter: 'brightness(0.8)' }}
                          />
                          <Box sx={{ 
                            position: 'absolute', 
                            top: 16, 
                            right: 16,
                            bgcolor: 'rgba(17, 24, 39, 0.8)',
                            backdropFilter: 'blur(4px)',
                            px: 2,
                            py: 0.5,
                            borderRadius: '8px',
                            border: '1px solid rgba(212, 175, 55, 0.3)',
                            zIndex: 2
                          }}>
                            <Typography variant="caption" sx={{ color: '#D4AF37', fontWeight: 700, textTransform: 'uppercase' }}>
                              {t(`activities.types.${activity.type?.toLowerCase()}`, activity.type)}
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Box sx={{ p: 4, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="caption" sx={{ color: '#9CA3AF', display: 'block', mb: 1 }}>
                            {activity.date ? new Date(activity.date).toLocaleDateString(dateLocale, { month: 'long', year: 'numeric' }) : ''}
                          </Typography>
                          <Typography variant="h5" sx={{ color: 'white', mb: 2, fontWeight: 700, lineHeight: 1.3 }}>
                            {activity.title}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#9CA3AF', lineHeight: 1.6, mb: 3, flexGrow: 1 }}>
                            {activity.tagline || (activity.description ? (activity.description.length > 100 ? `${activity.description.substring(0, 100)}...` : activity.description) : '')}
                          </Typography>
                          <Button 
                            variant="text" 
                            onClick={() => navigate(`/activities/${activity.id}`)}
                            sx={{ 
                              color: '#0FA3A3', 
                              p: 0, 
                              width: 'fit-content',
                              fontWeight: 600,
                              '&:hover': { bgcolor: 'transparent', color: '#D4AF37' }
                            }}
                          >
                            {t("home.activities.learnMore")}
                          </Button>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </AnimatePresence>
              </Grid>
            )}
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default HomePage;
